// Option A, first band — who is in the room.
// Prakriti, shift and the current medicine list decide what can be prescribed,
// so they belong above the scores rather than inside worksheet 01.
import React from "react";
import { useTranslation } from "react-i18next";
import { Section, Fact, Chip, Empty, Label } from "@/components/clinical/kit";
import { identity, concerns, tickedLabels, W02 } from "@/config/clinical";

function ChipList({ title, items, tone }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <div>
      <Label>{title}</Label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {items.map((x) => (
          <Chip key={x.index} tone={tone} title={x.note || undefined}>
            {t(x.label)}
            {x.note && <span className="text-[10px] italic opacity-70">· {x.note}</span>}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function MiniTable({ table }) {
  const { t } = useTranslation();
  if (!table.rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-[#E8EFE9] text-left">
          <tr>
            {table.columns.map((c) => (
              <th key={c} className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((cells, r) => (
            <tr key={r} className="border-t border-[#EFE9DE] align-top">
              {cells.map((c, i) => <td key={i} className="px-3 py-1.5 text-stone-700">{c || "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function IdentityStrip({ responses, participant }) {
  const { t } = useTranslation();
  const id = identity(responses);
  const c = concerns(responses);
  const exposures = tickedLabels(responses, "w01");
  const conditions = tickedLabels(responses, "w02", W02.conditions);
  const medicines = tickedLabels(responses, "w02", W02.medicines);
  const allergies = tickedLabels(responses, "w02", W02.allergies);
  const history = tickedLabels(responses, "w02", W02.history);
  const anything = [id.age, id.sex, id.prakriti, id.dept, id.shift].some(Boolean) ||
    exposures.length || conditions.length || medicines.length;

  return (
    <Section title="Who is in front of you" sub="Worksheets 01–03" testid="identity-strip">
      {!anything && <Empty>Intake worksheets 01–03 are not filled in yet.</Empty>}

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Fact label="Name" value={id.fullName || participant?.name} />
        <Fact label="Participant code" value={id.code || participant?.participant_code} />
        <Fact label="Age / DOB" value={id.age} />
        <Fact label="Sex" value={id.sex} />
        <Fact label="Prakriti" value={id.prakriti} />
        <Fact label="Department / designation" value={id.dept} />
        <Fact label="Shift pattern" value={id.shift} />
        <Fact label="Usual work hours" value={id.workHours} />
        <Fact label="Years in occupation" value={id.years} />
        <Fact label="Weekly days off" value={id.daysOff} />
        <Fact label="Commute" value={id.commute} />
      </div>

      {(allergies.length > 0 || medicines.length > 0) && (
        <div className="mt-4 space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3">
          <ChipList title="Allergies" items={allergies} tone="rose" />
          <ChipList title="Medicines in use" items={medicines} tone="amber" />
          <p className="text-[11px] text-stone-500">
            {t("Check every prescription against this list before adding a formulation.")}
          </p>
        </div>
      )}

      <div className="mt-4 space-y-3">
        <ChipList title="Diagnosed conditions" items={conditions} tone="sky" />
        <ChipList title="Surgical, injury and family history" items={history} />
        <ChipList title="Occupational exposures" items={exposures} />
      </div>

      {(c.concerns.rows.length > 0 || c.goals.rows.length > 0 || c.other) && (
        <div className="mt-5 space-y-3">
          <div>
            <Label>Main concerns in the participant's own words</Label>
            <div className="mt-1"><MiniTable table={c.concerns} /></div>
          </div>
          <div>
            <Label>90-day goals</Label>
            <div className="mt-1"><MiniTable table={c.goals} /></div>
          </div>
          {c.other && <p className="text-sm text-stone-700"><Label>Other</Label> <span>{c.other}</span></p>}
        </div>
      )}
    </Section>
  );
}
